import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import 'react-range-slider-input/dist/style.css';

import Toggle from 'components/Common/Toggle';
import { MLButton } from 'components/Button/Button';

interface ArchiveDatePopupProps {
  initialStart: string;
  initialEnd: string;
  minDate: Date;
  maxDate: Date;
  onConfirm: (start: string, end: string, mode: 'date' | 'period') => void;
  onClose: () => void;
  isDatePopup: boolean;
  setIsDatePopup: (isDatePopup: boolean) => void;
  onFilterChange: (filter: 'archive', value: boolean) => void;
  isLoading?: boolean;
}

const ArchiveDatePopup: React.FC<ArchiveDatePopupProps> = ({
  initialStart,
  initialEnd,
  minDate,
  maxDate,
  onConfirm,
  onClose,
  isDatePopup,
  setIsDatePopup,
  onFilterChange,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  const [localStart, setLocalStart] = useState(initialStart);
  const [localEnd, setLocalEnd] = useState(initialEnd);

  useEffect(() => {
    setLocalStart(initialStart);
    setLocalEnd(initialEnd);
  }, [initialStart, initialEnd, isDatePopup]);

  const handleStartDateChange = (newStart: string) => {
    const startDate = new Date(newStart);
    const endDate = new Date(localEnd);
    if (startDate > endDate) {
      setLocalEnd(newStart);
    }
    setLocalStart(newStart);
  };

  const handleEndDateChange = (newEnd: string) => {
    const startDate = new Date(localStart);
    const endDate = new Date(newEnd);
    if (endDate < startDate) {
      setLocalStart(newEnd);
    }
    setLocalEnd(newEnd);
  };
  const handleConfirmClick = () => {
    onFilterChange('archive', true);
    const mode = isDatePopup ? 'period' : 'date';

    const finalEnd = !isDatePopup ? localStart : localEnd;

    onConfirm(localStart, finalEnd, mode);
  };

  const handleClose = () => {
    onFilterChange('archive', false);
    onClose();
  };

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        background: 'white',
        border: '1px solid #ccc',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        zIndex: 1,
        cursor: 'default',
      }}
      onClick={e => e.stopPropagation()}
      onWheel={e => e.stopPropagation()}
      onTouchMove={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
    >
      <div className="border-bottom border-1">
        <div
          className="d-flex justify-content-between align-items-center pb-1"
          style={{ padding: '8px 16px', paddingBottom: '0px' }}
        >
          <div style={{ fontWeight: 700, fontSize: 18, color: '#3a3a6a' }}>{t('MapSettings.archive')}</div>
          <button
            className="btn btn-light waves-effect btn-sm fs-5 fw-semibold"
            style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0 }}
            onClick={handleClose}
          >
            <i className="ri-close-line" />
          </button>
        </div>
      </div>

      <div style={{ padding: '12px 16px' }}>
        <div className="d-flex align-items-center justify-content-center gap-2" style={{ marginBottom: 12 }}>
          <div className={!isDatePopup ? 'text-purple fw-medium' : ''}>{t('MapSettings.switcherDate')}</div>
          <Toggle
            checked={isDatePopup}
            onChange={checked => {
              setIsDatePopup(checked);
            }}
            size="sm"
          />
          <div className={isDatePopup ? 'text-purple fw-medium' : ''}>{t('MapSettings.switcherDatePeriod')}</div>
        </div>

        {isDatePopup ? (
          <div>
            <div style={{ fontSize: 13, color: '#3a3a6a', marginBottom: 2 }}>{t('MapSettings.from')}</div>
            <input
              type="date"
              value={localStart}
              min={minDate.toISOString().slice(0, 10)}
              max={maxDate.toISOString().slice(0, 10)}
              onChange={e => handleStartDateChange(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: 6,
                border: '1px solid #ccc',
                marginBottom: 8,
              }}
            />
            <div style={{ fontSize: 13, color: '#3a3a6a', marginBottom: 2 }}>{t('MapSettings.to')}</div>
            <input
              type="date"
              value={localEnd}
              min={minDate.toISOString().slice(0, 10)}
              max={maxDate.toISOString().slice(0, 10)}
              onChange={e => handleEndDateChange(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: 6,
                border: '1px solid #ccc',
                marginBottom: 8,
              }}
            />
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 13, color: '#3a3a6a', marginBottom: 2 }}>{t('MapSettings.singleDate')}</div>
            <input
              type="date"
              value={localStart}
              min={minDate.toISOString().slice(0, 10)}
              max={maxDate.toISOString().slice(0, 10)}
              onChange={e => handleStartDateChange(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: 6,
                border: '1px solid #ccc',
                marginBottom: 8,
              }}
            />
          </div>
        )}

        <MLButton
          color="success"
          isGhost
          classNames="w-100 mt-2"
          innerText={t('MapSettings.confirm')}
          onClick={handleConfirmClick}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default ArchiveDatePopup;
